import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import Layout from '../components/Layout';
import API_BASE_URL, { API_BASE_URL_WITHOUT_API } from '../config/api';
import './TourForm.css';

const TravelPackageForm = ({ mode = 'admin', ownerToken = '', ownerPhone = '', onSuccess, onCancel }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isOwnerMode = mode === 'owner';
  const isEdit = !!id && !isOwnerMode;
  const [activeTab, setActiveTab] = useState('basic');

  const [formData, setFormData] = useState({
    title: '',
    agencyName: '',
    description: '',
    shortDescription: '',
    city: '',
    state: '',
    country: 'India',
    zipCode: '',
    address: '',
    displayAddress: '',
    latitude: '',
    longitude: '',
    cityLatitude: '',
    cityLongitude: '',
    numberOfNights: '',
    numberOfDays: '',
    departureCity: '',
    whatsappNumber: '',
    phone: ownerPhone || '',
    email: '',
    website: '',
    cancellationPolicy: '',
    importantInfo: '',
    otherInfo: '',
    availableDatesType: 'all',
    currency: 'INR',
    status: isEdit ? 'active' : 'active',
    featured: false,
    verified: false
  });

  const [itinerary, setItinerary] = useState([]);
  const [inclusions, setInclusions] = useState([]);
  const [exclusions, setExclusions] = useState([]);
  const [packageVariants, setPackageVariants] = useState([]);
  const [specificDates, setSpecificDates] = useState([]);

  const [itineraryInput, setItineraryInput] = useState({ dayNumber: '', title: '', description: '', activities: '' });
  const [inclusionInput, setInclusionInput] = useState('');
  const [exclusionInput, setExclusionInput] = useState('');
  const [variantInput, setVariantInput] = useState({
    variantName: '',
    pricePerPerson: '',
    cutPrice: '',
    cancellationPolicyTag: '',
    description: ''
  });
  const [editingVariantIndex, setEditingVariantIndex] = useState(null);
  const [specificDateInput, setSpecificDateInput] = useState('');

  const [images, setImages] = useState([]);
  const [imagePreviews, setImagePreviews] = useState([]);
  const [existingImages, setExistingImages] = useState([]);
  const [featuredImage, setFeaturedImage] = useState('');

  const [citySuggestions, setCitySuggestions] = useState([]);
  const [showCitySuggestions, setShowCitySuggestions] = useState(false);
  const [cityInput, setCityInput] = useState('');

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (isEdit && id) fetchPackage();
  }, [id, isEdit]);

  useEffect(() => {
    return () => {
      imagePreviews.forEach(url => URL.revokeObjectURL(url));
    };
  }, [imagePreviews]);

  const fetchPackage = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/admin/travel-packages/${id}`);
      const pkg = response.data.data;
      setFormData({
        title: pkg.title || '',
        agencyName: pkg.agencyName || '',
        description: pkg.description || '',
        shortDescription: pkg.shortDescription || '',
        city: pkg.city || '',
        state: pkg.state || '',
        country: pkg.country || 'India',
        zipCode: pkg.zipCode || '',
        address: pkg.address || '',
        displayAddress: pkg.displayAddress || pkg.address || '',
        latitude: pkg.latitude ?? '',
        longitude: pkg.longitude ?? '',
        cityLatitude: pkg.cityLatitude ?? '',
        cityLongitude: pkg.cityLongitude ?? '',
        numberOfNights: pkg.numberOfNights ?? '',
        numberOfDays: pkg.numberOfDays ?? '',
        departureCity: pkg.departureCity || '',
        whatsappNumber: pkg.whatsappNumber || '',
        phone: pkg.phone || '',
        email: pkg.email || '',
        website: pkg.website || '',
        cancellationPolicy: pkg.cancellationPolicy || '',
        importantInfo: pkg.importantInfo || '',
        otherInfo: pkg.otherInfo || '',
        availableDatesType: pkg.availableDatesType || 'all',
        currency: pkg.currency || 'INR',
        status: pkg.status || 'active',
        featured: pkg.featured || false,
        verified: pkg.verified || false
      });
      setItinerary(pkg.itinerary || []);
      setInclusions(pkg.inclusions || []);
      setExclusions(pkg.exclusions || []);
      setPackageVariants(pkg.packageVariants || []);
      setSpecificDates(pkg.specificDates?.length ? pkg.specificDates.map(d => (typeof d === 'string' ? d : d?.toISOString?.() || d).split('T')[0]) : []);
      setExistingImages(pkg.images || []);
      setFeaturedImage(pkg.featuredImage || '');
      setCityInput(pkg.city || '');
    } catch (err) {
      setError('Failed to fetch travel package details');
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleCityInputChange = async (e) => {
    const value = e.target.value;
    setCityInput(value);
    setFormData(prev => ({ ...prev, city: value }));
    if (value.length > 2) {
      try {
        const response = await axios.get(`${API_BASE_URL}/public/place/autocomplete`, { params: { input: value } });
        setCitySuggestions(response.data.data || []);
        setShowCitySuggestions(true);
      } catch (err) {
        if (err.response?.status !== 400) setCitySuggestions([]);
      }
    } else {
      setCitySuggestions([]);
      setShowCitySuggestions(false);
    }
  };

  const handleCitySelect = async (place) => {
    setCityInput(place.description);
    setFormData(prev => ({ ...prev, city: place.description }));
    setShowCitySuggestions(false);
    setCitySuggestions([]);
    try {
      const response = await axios.get(`${API_BASE_URL}/public/place/details`, { params: { placeId: place.place_id } });
      const placeDetails = response.data.data;
      setFormData(prev => ({
        ...prev,
        city: place.description,
        state: placeDetails.state || prev.state,
        country: placeDetails.country || prev.country,
        cityLatitude: placeDetails.latitude ?? prev.cityLatitude,
        cityLongitude: placeDetails.longitude ?? prev.cityLongitude
      }));
    } catch (err) {
      console.error('Error fetching city place details:', err);
    }
  };

  const handleAddItineraryDay = () => {
    if (!itineraryInput.dayNumber || !itineraryInput.title) return;
    const activities = itineraryInput.activities
      ? itineraryInput.activities.split('\n').map(s => s.trim()).filter(Boolean)
      : [];
    const day = {
      dayNumber: parseInt(itineraryInput.dayNumber, 10),
      title: itineraryInput.title,
      description: itineraryInput.description || '',
      activities
    };
    setItinerary(prev => [...prev, day].sort((a, b) => a.dayNumber - b.dayNumber));
    setItineraryInput({ dayNumber: '', title: '', description: '', activities: '' });
  };

  const handleRemoveItineraryDay = (index) => {
    setItinerary(prev => prev.filter((_, i) => i !== index));
  };

  const handleAddInclusion = () => {
    if (inclusionInput.trim()) {
      setInclusions(prev => [...prev, inclusionInput.trim()]);
      setInclusionInput('');
    }
  };

  const handleRemoveInclusion = (index) => {
    setInclusions(prev => prev.filter((_, i) => i !== index));
  };

  const handleAddExclusion = () => {
    if (exclusionInput.trim()) {
      setExclusions(prev => [...prev, exclusionInput.trim()]);
      setExclusionInput('');
    }
  };

  const handleRemoveExclusion = (index) => {
    setExclusions(prev => prev.filter((_, i) => i !== index));
  };

  const handleAddVariant = () => {
    if (!variantInput.variantName || !variantInput.pricePerPerson) return;
    const v = {
      variantName: variantInput.variantName,
      pricePerPerson: parseFloat(variantInput.pricePerPerson),
      cutPrice: variantInput.cutPrice ? parseFloat(variantInput.cutPrice) : null,
      cancellationPolicyTag: variantInput.cancellationPolicyTag || '',
      description: variantInput.description || ''
    };
    if (editingVariantIndex !== null) {
      const updated = [...packageVariants];
      updated[editingVariantIndex] = v;
      setPackageVariants(updated);
      setEditingVariantIndex(null);
    } else {
      setPackageVariants(prev => [...prev, v]);
    }
    setVariantInput({ variantName: '', pricePerPerson: '', cutPrice: '', cancellationPolicyTag: '', description: '' });
  };

  const handleEditVariant = (index) => {
    const v = packageVariants[index];
    setVariantInput({
      variantName: v.variantName || '',
      pricePerPerson: v.pricePerPerson !== undefined ? String(v.pricePerPerson) : '',
      cutPrice: v.cutPrice != null ? String(v.cutPrice) : '',
      cancellationPolicyTag: v.cancellationPolicyTag || '',
      description: v.description || ''
    });
    setEditingVariantIndex(index);
  };

  const handleRemoveVariant = (index) => {
    if (editingVariantIndex === index) setEditingVariantIndex(null);
    setPackageVariants(prev => prev.filter((_, i) => i !== index));
  };

  const handleAddSpecificDate = () => {
    if (specificDateInput.trim()) {
      setSpecificDates(prev => prev.includes(specificDateInput.trim()) ? prev : [...prev, specificDateInput.trim()].sort());
      setSpecificDateInput('');
    }
  };

  const handleRemoveSpecificDate = (index) => {
    setSpecificDates(prev => prev.filter((_, i) => i !== index));
  };

  const handleImageChange = (e) => {
    const files = Array.from(e.target.files);
    setImages(files);
    setImagePreviews(files.map(f => URL.createObjectURL(f)));
  };

  const handleRemoveNewImage = (index) => {
    if (imagePreviews[index]) URL.revokeObjectURL(imagePreviews[index]);
    setImages(prev => prev.filter((_, i) => i !== index));
    setImagePreviews(prev => prev.filter((_, i) => i !== index));
  };

  const handleSetFeaturedFromNew = (index) => setFeaturedImage(`new_image_${index}`);
  const handleRemoveExistingImage = (index) => setExistingImages(prev => prev.filter((_, i) => i !== index));
  const handleSetFeaturedImage = (imagePath) => setFeaturedImage(imagePath);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    // Run validation for all tabs that have required fields
    const tabsToValidate = ['basic', 'destination', 'variants', 'contact', 'dates', 'media'];
    const allErrors = [];
    for (const tabId of tabsToValidate) {
      const tabErrors = validateCurrentTab(tabId);
      if (tabErrors.length > 0) allErrors.push(...tabErrors);
    }
    if (allErrors.length > 0) {
      setError(allErrors.join('. '));
      setLoading(false);
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    try {
      const submitData = new FormData();
      submitData.append('title', formData.title);
      submitData.append('agencyName', formData.agencyName?.trim() || '');
      submitData.append('description', formData.description);
      submitData.append('shortDescription', formData.shortDescription);
      submitData.append('city', formData.city);
      submitData.append('state', formData.state);
      submitData.append('country', formData.country);
      submitData.append('zipCode', formData.zipCode);
      submitData.append('address', formData.address);
      submitData.append('displayAddress', formData.displayAddress || formData.address);
      submitData.append('latitude', formData.latitude);
      submitData.append('longitude', formData.longitude);
      submitData.append('cityLatitude', formData.cityLatitude);
      submitData.append('cityLongitude', formData.cityLongitude);
      submitData.append('numberOfNights', formData.numberOfNights);
      submitData.append('numberOfDays', formData.numberOfDays);
      submitData.append('departureCity', formData.departureCity);
      submitData.append('itinerary', JSON.stringify(itinerary));
      submitData.append('inclusions', JSON.stringify(inclusions));
      submitData.append('exclusions', JSON.stringify(exclusions));
      submitData.append('packageVariants', JSON.stringify(packageVariants));
      submitData.append('whatsappNumber', formData.whatsappNumber);
      submitData.append('phone', formData.phone);
      submitData.append('email', formData.email);
      submitData.append('website', formData.website);
      submitData.append('cancellationPolicy', formData.cancellationPolicy);
      submitData.append('importantInfo', formData.importantInfo);
      submitData.append('otherInfo', formData.otherInfo);
      submitData.append('availableDatesType', formData.availableDatesType || 'all');
      submitData.append('specificDates', JSON.stringify(specificDates));
      submitData.append('currency', formData.currency);
      submitData.append('status', formData.status);
      submitData.append('featured', formData.featured);
      submitData.append('verified', formData.verified);
      submitData.append('existingImages', JSON.stringify(existingImages));

      let finalFeaturedImage = featuredImage;
      let imagesToUpload = [...images];
      if (finalFeaturedImage && String(finalFeaturedImage).startsWith('new_image_')) {
        const idx = parseInt(String(finalFeaturedImage).replace('new_image_', ''), 10);
        if (idx >= 0 && idx < imagesToUpload.length) {
          const [feat] = imagesToUpload.splice(idx, 1);
          imagesToUpload.unshift(feat);
          imagePreviews.forEach(url => URL.revokeObjectURL(url));
          finalFeaturedImage = '';
        }
      }
      if (!finalFeaturedImage && existingImages.length > 0) finalFeaturedImage = existingImages[0];
      submitData.append('featuredImage', finalFeaturedImage || '');
      imagesToUpload.forEach(file => submitData.append('images', file));

      if (isOwnerMode) {
        await axios.post(`${API_BASE_URL}/public/owners/travel-packages`, submitData, {
          headers: {
            'Content-Type': 'multipart/form-data',
            Authorization: `Bearer ${ownerToken}`
          }
        });
        if (onSuccess) onSuccess();
      } else if (isEdit) {
        await axios.put(`${API_BASE_URL}/admin/travel-packages/${id}`, submitData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        navigate('/sy-admin/travel-packages');
      } else {
        await axios.post(`${API_BASE_URL}/admin/travel-packages`, submitData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        navigate('/sy-admin/travel-packages');
      }
    } catch (err) {
      setError(err.response?.data?.message || err.response?.data?.error || 'Failed to save travel package');
      setLoading(false);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const tabs = [
    { id: 'basic', label: 'Basic' },
    { id: 'destination', label: 'Destination' },
    { id: 'duration', label: 'Duration & Departure' },
    { id: 'itinerary', label: 'Itinerary' },
    { id: 'inclusions', label: 'Inclusions / Exclusions' },
    { id: 'variants', label: 'Package Variants' },
    { id: 'contact', label: 'Contact' },
    { id: 'policies', label: 'Policies' },
    { id: 'dates', label: 'Valid Dates' },
    { id: 'media', label: 'Media' },
    { id: 'status', label: 'Status' }
  ];

  const isLastTab = activeTab === tabs[tabs.length - 1].id;
  const currentTabIndex = tabs.findIndex(tab => tab.id === activeTab);
  const nextTab = currentTabIndex < tabs.length - 1 ? tabs[currentTabIndex + 1] : null;

  const validateCurrentTab = (tabId) => {
    const errors = [];
    switch (tabId) {
      case 'basic':
        if (!formData.title || !formData.title.trim()) errors.push('Package title is required');
        if (!formData.agencyName || !formData.agencyName.trim()) errors.push('Agency name is required');
        break;
      case 'destination':
        if (!formData.city || !formData.city.trim()) errors.push('Destination city is required');
        break;
      case 'duration':
        // Optional; no required fields
        break;
      case 'itinerary':
        // Optional
        break;
      case 'inclusions':
        // Optional
        break;
      case 'variants':
        if (!packageVariants || packageVariants.length === 0) {
          errors.push('At least one package variant is required (e.g. Standard, Deluxe with price per person)');
        } else {
          const invalid = packageVariants.filter(v => !v.variantName || !v.variantName.trim() || v.pricePerPerson == null || v.pricePerPerson === '');
          if (invalid.length > 0) {
            errors.push('Each variant must have a name and price per person');
          }
        }
        break;
      case 'contact':
        if (!formData.whatsappNumber || !formData.whatsappNumber.trim()) errors.push('WhatsApp number is required');
        if (!formData.phone || !formData.phone.trim()) errors.push('Phone number is required');
        break;
      case 'policies':
        break;
      case 'dates':
        if (formData.availableDatesType === 'specific' && (!specificDates || specificDates.length === 0)) {
          errors.push('Add at least one specific date when "Specific dates only" is selected');
        }
        break;
      case 'media':
        if (images.length + existingImages.length === 0) {
          errors.push('At least one image is required');
        }
        break;
      case 'status':
        break;
      default:
        break;
    }
    return errors;
  };

  const handleNext = (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    if (isLastTab) return;
    const validationErrors = validateCurrentTab(activeTab);
    if (validationErrors.length > 0) {
      setError(validationErrors.join('. '));
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }
    setError('');
    if (nextTab) {
      setActiveTab(nextTab.id);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const content = (
    <div className="container">
        <div className="tour-form-header">
          <h1>{isEdit ? 'Edit Travel Package' : 'Add Travel Package'}</h1>
        </div>

        {error && (
          <div className="error-message" style={{
            padding: '16px 20px', backgroundColor: '#fee2e2', border: '1px solid #fca5a5',
            borderRadius: '8px', color: '#991b1b', marginBottom: '24px', display: 'flex', alignItems: 'center', gap: '12px'
          }}>
            <span style={{ fontSize: '20px' }}>⚠️</span>
            <span>{error}</span>
          </div>
        )}

        <form onSubmit={handleSubmit} className="tour-form">
          <div className="form-tabs">
            {tabs.map(tab => (
              <button
                key={tab.id}
                type="button"
                className={`tab-btn ${activeTab === tab.id ? 'active' : ''}`}
                onClick={() => setActiveTab(tab.id)}
              >
                {tab.label}
              </button>
            ))}
          </div>

          <div className="tab-content">
            {activeTab === 'basic' && (
              <div className="form-section">
                <h2>Basic Information</h2>
                <div className="form-group">
                  <label>Package Title *</label>
                  <input type="text" name="title" value={formData.title} onChange={handleChange} placeholder="e.g. 3 Nights Goa Holiday" required />
                </div>
                <div className="form-group">
                  <label>Agency name *</label>
                  <input type="text" name="agencyName" value={formData.agencyName} onChange={handleChange} placeholder="e.g. Sunshine Tours" required />
                </div>
                <div className="form-group">
                  <label>Short Description</label>
                  <input type="text" name="shortDescription" value={formData.shortDescription} onChange={handleChange} placeholder="Brief tagline for listings" maxLength={200} />
                </div>
                <div className="form-group">
                  <label>Full Description</label>
                  <textarea name="description" value={formData.description} onChange={handleChange} rows={6} placeholder="Overview, highlights, what's included" />
                </div>
                <div className="form-group">
                  <label>Currency</label>
                  <select name="currency" value={formData.currency} onChange={handleChange}>
                    <option value="INR">INR (₹)</option>
                    <option value="USD">USD ($)</option>
                    <option value="EUR">EUR (€)</option>
                    <option value="GBP">GBP (£)</option>
                  </select>
                </div>
              </div>
            )}

            {activeTab === 'destination' && (
              <div className="form-section">
                <h2>Destination</h2>
                <div className="form-group" style={{ position: 'relative' }}>
                  <label>Destination City *</label>
                  <input
                    type="text"
                    value={cityInput || formData.city}
                    onChange={handleCityInputChange}
                    onBlur={() => setTimeout(() => setShowCitySuggestions(false), 200)}
                    onFocus={() => citySuggestions.length > 0 && setShowCitySuggestions(true)}
                    placeholder="e.g. Goa, Shimla"
                    required
                  />
                  {showCitySuggestions && citySuggestions.length > 0 && (
                    <div className="suggestions-dropdown">
                      {citySuggestions.map((suggestion, idx) => (
                        <div key={idx} className="suggestion-item" onClick={() => handleCitySelect(suggestion)}>
                          {suggestion.structured_formatting?.main_text || suggestion.description}
                          {suggestion.structured_formatting?.secondary_text && (
                            <div className="suggestion-secondary">{suggestion.structured_formatting.secondary_text}</div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>State</label>
                    <input type="text" name="state" value={formData.state} onChange={handleChange} />
                  </div>
                  <div className="form-group">
                    <label>Country</label>
                    <input type="text" name="country" value={formData.country} onChange={handleChange} />
                  </div>
                </div>
                <div className="form-group">
                  <label>Address (optional)</label>
                  <input type="text" name="address" value={formData.address} onChange={handleChange} placeholder="Full address" />
                </div>
                <div className="form-group">
                  <label>Display Address</label>
                  <input type="text" name="displayAddress" value={formData.displayAddress} onChange={handleChange} />
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>Latitude</label>
                    <input type="number" name="latitude" value={formData.latitude} onChange={handleChange} step="any" />
                  </div>
                  <div className="form-group">
                    <label>Longitude</label>
                    <input type="number" name="longitude" value={formData.longitude} onChange={handleChange} step="any" />
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>City Latitude</label>
                    <input type="number" name="cityLatitude" value={formData.cityLatitude} onChange={handleChange} step="any" />
                  </div>
                  <div className="form-group">
                    <label>City Longitude</label>
                    <input type="number" name="cityLongitude" value={formData.cityLongitude} onChange={handleChange} step="any" />
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'duration' && (
              <div className="form-section">
                <h2>Duration & Departure</h2>
                <div className="form-row">
                  <div className="form-group">
                    <label>Number of Nights</label>
                    <input type="number" name="numberOfNights" value={formData.numberOfNights} onChange={handleChange} min={0} placeholder="e.g. 2" />
                  </div>
                  <div className="form-group">
                    <label>Number of Days</label>
                    <input type="number" name="numberOfDays" value={formData.numberOfDays} onChange={handleChange} min={0} placeholder="e.g. 3" />
                  </div>
                </div>
                <div className="form-group">
                  <label>Departure City (Ex-?)</label>
                  <input type="text" name="departureCity" value={formData.departureCity} onChange={handleChange} placeholder="e.g. New Delhi, Mumbai" />
                </div>
              </div>
            )}

            {activeTab === 'itinerary' && (
              <div className="form-section">
                <h2>Day-wise Itinerary</h2>
                <p className="form-hint" style={{ marginBottom: '16px' }}>Add each day of the trip with title and description.</p>
                <div className="form-row">
                  <div className="form-group" style={{ maxWidth: '80px' }}>
                    <label>Day #</label>
                    <input
                      type="number"
                      min={1}
                      value={itineraryInput.dayNumber}
                      onChange={e => setItineraryInput(prev => ({ ...prev, dayNumber: e.target.value }))}
                    />
                  </div>
                  <div className="form-group" style={{ flex: 1 }}>
                    <label>Title</label>
                    <input
                      type="text"
                      value={itineraryInput.title}
                      onChange={e => setItineraryInput(prev => ({ ...prev, title: e.target.value }))}
                      placeholder="e.g. Arrival – Goa"
                    />
                  </div>
                </div>
                <div className="form-group">
                  <label>Description</label>
                  <textarea
                    value={itineraryInput.description}
                    onChange={e => setItineraryInput(prev => ({ ...prev, description: e.target.value }))}
                    rows={3}
                    placeholder="What happens on this day"
                  />
                </div>
                <div className="form-group">
                  <label>Activities (one per line)</label>
                  <textarea
                    value={itineraryInput.activities}
                    onChange={e => setItineraryInput(prev => ({ ...prev, activities: e.target.value }))}
                    rows={2}
                    placeholder="Sightseeing&#10;Lunch at..."
                  />
                </div>
                <button type="button" onClick={handleAddItineraryDay} className="btn btn-primary">Add Day</button>
                <div className="options-list" style={{ marginTop: '24px' }}>
                  {itinerary.length === 0 ? (
                    <p className="form-hint">No days added yet.</p>
                  ) : (
                    itinerary.map((day, idx) => (
                      <div key={idx} className="option-item">
                        <div>
                          <strong>Day {day.dayNumber}: {day.title}</strong>
                          {day.description && <p style={{ margin: '4px 0 0', fontSize: '13px', color: '#64748b' }}>{day.description}</p>}
                        </div>
                        <button type="button" onClick={() => handleRemoveItineraryDay(idx)} className="btn btn-danger btn-sm">Remove</button>
                      </div>
                    ))
                  )}
                </div>
              </div>
            )}

            {activeTab === 'inclusions' && (
              <div className="form-section">
                <h2>Inclusions & Exclusions</h2>
                <div className="form-group">
                  <label>Inclusions</label>
                  <div className="amenity-input">
                    <input
                      type="text"
                      value={inclusionInput}
                      onChange={e => setInclusionInput(e.target.value)}
                      onKeyPress={e => e.key === 'Enter' && (e.preventDefault(), handleAddInclusion())}
                      placeholder="e.g. Accommodation, Breakfast"
                    />
                    <button type="button" onClick={handleAddInclusion} className="btn btn-secondary">Add</button>
                  </div>
                  <div className="amenity-list" style={{ marginTop: 8 }}>
                    {inclusions.map((item, idx) => (
                      <span key={idx} className="badge badge-primary">
                        {item}
                        <button type="button" onClick={() => handleRemoveInclusion(idx)} className="remove-btn">×</button>
                      </span>
                    ))}
                  </div>
                </div>
                <div className="form-group">
                  <label>Exclusions</label>
                  <div className="amenity-input">
                    <input
                      type="text"
                      value={exclusionInput}
                      onChange={e => setExclusionInput(e.target.value)}
                      onKeyPress={e => e.key === 'Enter' && (e.preventDefault(), handleAddExclusion())}
                      placeholder="e.g. Flights, Visa"
                    />
                    <button type="button" onClick={handleAddExclusion} className="btn btn-secondary">Add</button>
                  </div>
                  <div className="amenity-list" style={{ marginTop: 8 }}>
                    {exclusions.map((item, idx) => (
                      <span key={idx} className="badge badge-secondary">
                        {item}
                        <button type="button" onClick={() => handleRemoveExclusion(idx)} className="remove-btn">×</button>
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'variants' && (
              <div className="form-section">
                <h2>Package Variants (Pricing)</h2>
                <p className="form-hint" style={{ marginBottom: '16px' }}>e.g. Standard, Deluxe with different price per person.</p>
                <div className="form-row">
                  <div className="form-group">
                    <label>Variant Name *</label>
                    <input
                      type="text"
                      value={variantInput.variantName}
                      onChange={e => setVariantInput(prev => ({ ...prev, variantName: e.target.value }))}
                      placeholder="e.g. Standard, Deluxe"
                    />
                  </div>
                  <div className="form-group">
                    <label>Price per Person *</label>
                    <input
                      type="number"
                      min={0}
                      step={0.01}
                      value={variantInput.pricePerPerson}
                      onChange={e => setVariantInput(prev => ({ ...prev, pricePerPerson: e.target.value }))}
                    />
                  </div>
                  <div className="form-group">
                    <label>Cut Price (Original)</label>
                    <input
                      type="number"
                      min={0}
                      step={0.01}
                      value={variantInput.cutPrice}
                      onChange={e => setVariantInput(prev => ({ ...prev, cutPrice: e.target.value }))}
                    />
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>Cancellation Tag</label>
                    <input
                      type="text"
                      value={variantInput.cancellationPolicyTag}
                      onChange={e => setVariantInput(prev => ({ ...prev, cancellationPolicyTag: e.target.value }))}
                      placeholder="e.g. Free cancellation"
                    />
                  </div>
                </div>
                <div className="form-group">
                  <label>Description</label>
                  <textarea
                    value={variantInput.description}
                    onChange={e => setVariantInput(prev => ({ ...prev, description: e.target.value }))}
                    rows={2}
                  />
                </div>
                <div style={{ display: 'flex', gap: '12px' }}>
                  <button type="button" onClick={handleAddVariant} className="btn btn-primary">
                    {editingVariantIndex !== null ? 'Update Variant' : 'Add Variant'}
                  </button>
                  {editingVariantIndex !== null && (
                    <button type="button" onClick={() => { setEditingVariantIndex(null); setVariantInput({ variantName: '', pricePerPerson: '', cutPrice: '', cancellationPolicyTag: '', description: '' }); }} className="btn btn-secondary">Cancel</button>
                  )}
                </div>
                <div className="options-list" style={{ marginTop: '24px' }}>
                  {packageVariants.length === 0 ? (
                    <p className="form-hint">No variants yet.</p>
                  ) : (
                    packageVariants.map((v, idx) => (
                      <div key={idx} className="option-item">
                        <div>
                          <strong>{v.variantName}</strong> — {formData.currency} {v.pricePerPerson} per person
                          {v.cutPrice != null && <span style={{ textDecoration: 'line-through', color: '#64748b', marginLeft: '8px' }}>{formData.currency} {v.cutPrice}</span>}
                        </div>
                        <div style={{ display: 'flex', gap: '8px' }}>
                          <button type="button" onClick={() => handleEditVariant(idx)} className="btn btn-secondary btn-sm">Edit</button>
                          <button type="button" onClick={() => handleRemoveVariant(idx)} className="btn btn-danger btn-sm">Remove</button>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            )}

            {activeTab === 'contact' && (
              <div className="form-section">
                <h2>Contact</h2>
                <div className="form-group">
                  <label>WhatsApp Number *</label>
                  <input type="text" name="whatsappNumber" value={formData.whatsappNumber} onChange={handleChange} required placeholder="+919876543210" />
                </div>
                <div className="form-group">
                  <label>Phone *</label>
                  <input
                    type="text"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    readOnly={isOwnerMode}
                    placeholder="e.g., +919876543210"
                    required
                  />
                  {isOwnerMode && (
                    <small className="form-hint">This number is used for listing authentication.</small>
                  )}
                </div>
                <div className="form-group">
                  <label>Email</label>
                  <input type="email" name="email" value={formData.email} onChange={handleChange} />
                </div>
                <div className="form-group">
                  <label>Website</label>
                  <input type="url" name="website" value={formData.website} onChange={handleChange} />
                </div>
              </div>
            )}

            {activeTab === 'policies' && (
              <div className="form-section">
                <h2>Policies</h2>
                <div className="form-group">
                  <label>Cancellation Policy</label>
                  <textarea name="cancellationPolicy" value={formData.cancellationPolicy} onChange={handleChange} rows={4} />
                </div>
                <div className="form-group">
                  <label>Important Info</label>
                  <textarea name="importantInfo" value={formData.importantInfo} onChange={handleChange} rows={4} />
                </div>
                <div className="form-group">
                  <label>Other Info</label>
                  <textarea name="otherInfo" value={formData.otherInfo} onChange={handleChange} rows={4} />
                </div>
              </div>
            )}

            {activeTab === 'dates' && (
              <div className="form-section">
                <h2>Valid Travel Dates</h2>
                <div className="form-group">
                  <label>Available dates</label>
                  <select name="availableDatesType" value={formData.availableDatesType} onChange={handleChange}>
                    <option value="all">All dates</option>
                    <option value="specific">Specific dates only</option>
                  </select>
                </div>
                {formData.availableDatesType === 'specific' && (
                  <div className="form-group">
                    <label>Add specific dates (YYYY-MM-DD)</label>
                    <div className="amenity-input">
                      <input type="date" value={specificDateInput} onChange={e => setSpecificDateInput(e.target.value)} />
                      <button type="button" onClick={handleAddSpecificDate} className="btn btn-secondary">Add</button>
                    </div>
                    <div className="amenity-list" style={{ marginTop: 8 }}>
                      {specificDates.map((d, idx) => (
                        <span key={idx} className="badge badge-primary">
                          {d}
                          <button type="button" onClick={() => handleRemoveSpecificDate(idx)} className="remove-btn">×</button>
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {activeTab === 'media' && (
              <div className="form-section">
                <h2>Images</h2>
                <div className="form-group">
                  <label>Upload Images *</label>
                  <div className="image-upload-area">
                    <input type="file" accept="image/*" multiple onChange={handleImageChange} id="pkg-image-upload" className="image-upload-input" />
                    <label htmlFor="pkg-image-upload" className="image-upload-label">
                      <div className="image-upload-content">
                        <span className="image-upload-icon">📷</span>
                        <span className="image-upload-text">Click to upload</span>
                        <span className="image-upload-hint">PNG, JPG, GIF up to 5MB</span>
                      </div>
                    </label>
                  </div>
                </div>
                {imagePreviews.length > 0 && (
                  <div className="images-preview-section">
                    <h3>New images</h3>
                    <div className="images-grid">
                      {imagePreviews.map((preview, idx) => (
                        <div key={idx} className="image-preview-card">
                          <div className="image-preview-wrapper">
                            <img src={preview} alt={`Preview ${idx + 1}`} />
                            {featuredImage === `new_image_${idx}` && <div className="featured-badge-overlay"><span>⭐ Featured</span></div>}
                          </div>
                          <div className="image-preview-actions">
                            <button type="button" onClick={() => handleSetFeaturedFromNew(idx)} className={`btn btn-sm ${featuredImage === `new_image_${idx}` ? 'btn-primary' : 'btn-secondary'}`}>{featuredImage === `new_image_${idx}` ? '⭐ Featured' : 'Set Featured'}</button>
                            <button type="button" onClick={() => handleRemoveNewImage(idx)} className="btn btn-sm btn-danger">Remove</button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {existingImages.length > 0 && (
                  <div className="images-preview-section">
                    <h3>Existing images</h3>
                    <div className="images-grid">
                      {existingImages.map((image, idx) => (
                        <div key={idx} className="image-preview-card">
                          <div className="image-preview-wrapper">
                            <img src={`${API_BASE_URL_WITHOUT_API}${image}`} alt={`Package ${idx + 1}`} />
                            {featuredImage === image && <div className="featured-badge-overlay"><span>⭐ Featured</span></div>}
                          </div>
                          <div className="image-preview-actions">
                            <button type="button" onClick={() => handleSetFeaturedImage(image)} className={`btn btn-sm ${featuredImage === image ? 'btn-primary' : 'btn-secondary'}`}>{featuredImage === image ? '⭐ Featured' : 'Set Featured'}</button>
                            <button type="button" onClick={() => handleRemoveExistingImage(idx)} className="btn btn-sm btn-danger">Remove</button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {imagePreviews.length === 0 && existingImages.length === 0 && <p className="form-hint" style={{ marginTop: '16px' }}>Upload at least one image.</p>}
              </div>
            )}

            {activeTab === 'status' && (
              <div className="form-section">
                <h2>Status</h2>
                <div className="form-group">
                  <label>Status</label>
                  <select name="status" value={formData.status} onChange={handleChange}>
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                    <option value="pending">Pending</option>
                  </select>
                </div>
                <div className="form-row">
                  <label className="checkbox-label">
                    <input type="checkbox" name="featured" checked={formData.featured} onChange={handleChange} />
                    <span>Featured</span>
                  </label>
                  <label className="checkbox-label">
                    <input type="checkbox" name="verified" checked={formData.verified} onChange={handleChange} />
                    <span>Verified</span>
                  </label>
                </div>
              </div>
            )}
          </div>

          <div className="form-actions">
            {isLastTab ? (
              <button type="submit" className="btn btn-primary" disabled={loading}>
                {loading ? 'Saving...' : isEdit ? 'Update Package' : isOwnerMode ? 'Submit listing' : 'Create Package'}
              </button>
            ) : (
              <button
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  handleNext(e);
                }}
                className="btn btn-primary"
              >
                Next
              </button>
            )}
            <button type="button" onClick={() => (isOwnerMode && onCancel ? onCancel() : navigate('/sy-admin/travel-packages'))} className="btn btn-secondary">Cancel</button>
          </div>
        </form>
      </div>
  );

  return isOwnerMode ? content : <Layout>{content}</Layout>;
};

export default TravelPackageForm;
